import type { ProfileResult } from '@obras/shared';
import { MIN_PASSWORD_LENGTH, ROLE_LABELS } from '@obras/shared';
import { useMutation } from '@tanstack/react-query';
import { Check, KeyRound, UserRound } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { AppShell, PageHeader } from '../components/AppShell.js';
import { Button } from '../components/ui/Button.js';
import { Card, FormSection } from '../components/ui/Card.js';
import { PasswordField, TextField } from '../components/ui/Field.js';
import { Avatar, FormError, Notice } from '../components/ui/Feedback.js';
import { ApiError, api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

const ICON = { strokeWidth: 1.5 } as const;

/**
 * Uma linha de dado da conta: rótulo à esquerda, valor à direita.
 *
 * Não é campo — é o que a pessoa não altera daqui. Papel e órgão são decisão de
 * quem administra, e mostrá-los como caixa de texto desabilitada convidaria a
 * tentar.
 */
function Dado({ termo, children }: { termo: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-[13px] text-muted-foreground">{termo}</dt>
      {/* Quebra em vez de cortar: nome de município e de ambiente são longos. */}
      <dd className="min-w-0 text-right text-[13px]">{children}</dd>
    </div>
  );
}

/**
 * A própria conta, num formulário só.
 *
 * Eram dois cartões com um botão cada, porque a troca de senha derruba as
 * sessões e a alteração cadastral não. Só que a pessoa que abre esta tela não
 * chegou para "executar uma das duas operações": chegou para acertar a própria
 * conta. Ter de escolher entre dois botões de salvar é obrigá-la a saber, antes
 * de mexer em nada, qual metade do que ela digitou vai por qual caminho.
 *
 * O que separa as duas continua existindo, só que como **regra** e não como
 * botão: a senha nova em branco mantém a senha atual, e a troca de senha é a
 * última coisa a acontecer justamente porque encerra a sessão que está salvando.
 *
 * O layout é de duas colunas: à esquerda o que a conta **é** — e que só quem
 * administra muda —, à direita o que ela edita. Empilhar os dois numa coluna
 * estreita deixava dois terços da tela vazios e obrigava a rolar para chegar ao
 * botão.
 */
export function PerfilPage() {
  const { user, organization, environments, perfilAlterado, logout } = useAuth();

  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirmacao, setConfirmacao] = useState('');

  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [salvo, setSalvo] = useState(false);

  const salvarDados = useMutation({
    mutationFn: (body: { name: string; email: string; currentPassword?: string }) =>
      api.patch<ProfileResult>('/perfil', body),
  });

  const trocarSenha = useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api.post<void>('/auth/trocar-senha', body),
  });

  const nomeMudou = name.trim() !== (user?.name ?? '');
  const emailMudou = email.trim() !== (user?.email ?? '');
  const querTrocarSenha = nova !== '' || confirmacao !== '';
  const mudou = nomeMudou || emailMudou || querTrocarSenha;

  /* Alterar o nome não pede senha; alterar o que dá acesso à conta, sim. */
  const precisaDaSenhaAtual = emailMudou || querTrocarSenha;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});
    setSalvo(false);

    /*
     * A confirmação é conferida aqui, e não no servidor: é erro de digitação,
     * não regra de negócio. Mandá-la à rede para ouvir "não confere" seria uma
     * viagem para dizer o que a tela já sabe.
     */
    if (querTrocarSenha && nova !== confirmacao) {
      setFieldErrors({ confirmacao: 'A confirmação não confere com a nova senha.' });
      return;
    }

    if (precisaDaSenhaAtual && atual === '') {
      setFieldErrors({
        currentPassword: emailMudou
          ? 'Informe a senha atual para alterar o e-mail.'
          : 'Informe a senha atual para trocar a senha.',
      });
      return;
    }

    let dadosSalvos = false;

    try {
      if (nomeMudou || emailMudou) {
        const perfil = await salvarDados.mutateAsync({
          name: name.trim(),
          email: email.trim(),
          ...(emailMudou ? { currentPassword: atual } : {}),
        });
        perfilAlterado({ name: perfil.name, email: perfil.email });
        dadosSalvos = true;
      }

      /*
       * Por último, e não por organização do código: trocar a senha encerra
       * todas as sessões, inclusive a que está enviando este formulário. Fosse
       * a primeira chamada, a segunda sairia com um token já morto.
       */
      if (querTrocarSenha) {
        await trocarSenha.mutateAsync({ currentPassword: atual, newPassword: nova });
        await logout();
        return;
      }

      setAtual('');
      setSalvo(true);
    } catch (error) {
      const naSenha = dadosSalvos ? 'Nome e e-mail foram salvos, mas a senha não mudou. ' : '';

      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors());
        setFormError(
          error.details
            ? `${naSenha}Verifique os campos destacados.`
            : `${naSenha}${error.message}`,
        );
      } else {
        setFormError(`${naSenha}Não foi possível salvar.`);
      }
    }
  }

  /*
   * Os outros ambientes pelo nome, e não pela contagem: "mais 1" obriga a abrir
   * o menu para descobrir qual é. O da plataforma chega como "Administrador",
   * que ao lado de nomes de município se lê como pessoa.
   */
  const outrosAmbientes = environments
    .filter((ambiente) => ambiente.id !== organization?.id)
    .map((ambiente) =>
      ambiente.kind === 'platform' ? 'Administração da plataforma' : ambiente.name,
    );

  return (
    <AppShell>
      <PageHeader title="Perfil" description="Seus dados de acesso ao sistema." />

      <div className="grid items-start gap-4 lg:grid-cols-[19rem_minmax(0,1fr)]">
        {/*
          A coluna da esquerda é o que a conta é, e não o que ela edita: papel e
          órgão vêm de quem administra. Acompanha a rolagem porque é a referência
          de quem está preenchendo os campos ao lado.
        */}
        <Card className="flex flex-col gap-5 p-6 lg:sticky lg:top-9">
          <div className="flex items-center gap-3.5">
            <Avatar name={user?.name ?? ''} url={user?.avatarUrl ?? null} size={52} />
            <div className="min-w-0">
              <p className="truncate text-[15px] font-medium">{user?.name}</p>
              <p className="truncate text-[13px] text-muted-foreground">{user?.email}</p>
            </div>
          </div>

          <dl className="flex flex-col gap-2">
            <Dado termo="Papel">
              {organization ? ROLE_LABELS[user?.role ?? 'leitor'] : 'Administração da plataforma'}
            </Dado>
            {organization ? <Dado termo="Órgão">{organization.name}</Dado> : null}
            {user?.jobTitle ? <Dado termo="Cargo">{user.jobTitle}</Dado> : null}
            {outrosAmbientes.length > 0 ? (
              <Dado termo="Também entra em">{outrosAmbientes.join(', ')}</Dado>
            ) : null}
          </dl>
        </Card>

        <form
          onSubmit={(event) => void handleSubmit(event)}
          className="flex flex-col gap-4"
          noValidate
        >
          <FormError>{formError}</FormError>

          {salvo && !mudou ? <Notice icon={Check}>Perfil atualizado.</Notice> : null}

          <FormSection
            icon={UserRound}
            title="Identificação"
            description="Como você aparece para o restante da equipe nos relatórios, nas visitas e na auditoria."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                label="Nome"
                required
                autoComplete="name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setSalvo(false);
                }}
                {...(fieldErrors['name'] ? { error: fieldErrors['name'] } : {})}
              />

              <TextField
                label="E-mail"
                type="email"
                required
                autoComplete="email"
                hint="Utilizado para acesso ao sistema e recuperação de senha."
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setSalvo(false);
                }}
                {...(fieldErrors['email'] ? { error: fieldErrors['email'] } : {})}
              />
            </div>
          </FormSection>

          <FormSection
            icon={KeyRound}
            title="Acesso"
            description="Deixe a nova senha em branco para manter a atual. Ao trocá-la, todas as sessões abertas nesta conta são encerradas — inclusive esta."
          >
            {/*
              Um abaixo do outro, na ordem em que se preenchem: confirmar quem
              é, escolher a nova, repetir. A largura fica na medida do que se
              digita — campo de senha esticado por 800px não ajuda ninguém.
            */}
            <div className="flex flex-col gap-4 sm:max-w-md">
              <PasswordField
                label="Senha atual"
                autoComplete="current-password"
                required={precisaDaSenhaAtual}
                hint="Exigida para trocar o e-mail ou a senha."
                value={atual}
                onChange={(event) => {
                  setAtual(event.target.value);
                  setSalvo(false);
                }}
                {...(fieldErrors['currentPassword']
                  ? { error: fieldErrors['currentPassword'] }
                  : {})}
              />

              <PasswordField
                label="Nova senha"
                autoComplete="new-password"
                hint={`Mínimo de ${MIN_PASSWORD_LENGTH} caracteres.`}
                value={nova}
                onChange={(event) => {
                  setNova(event.target.value);
                  setSalvo(false);
                }}
                {...(fieldErrors['newPassword'] ? { error: fieldErrors['newPassword'] } : {})}
              />

              <PasswordField
                label="Repita a nova senha"
                autoComplete="new-password"
                required={querTrocarSenha}
                value={confirmacao}
                onChange={(event) => {
                  setConfirmacao(event.target.value);
                  setSalvo(false);
                }}
                {...(fieldErrors['confirmacao'] ? { error: fieldErrors['confirmacao'] } : {})}
              />
            </div>
          </FormSection>

          <div className="flex items-center justify-end gap-3">
            {mudou ? (
              <span className="text-[13px] text-muted-foreground">
                {querTrocarSenha
                  ? 'Ao salvar, você entrará de novo com a senha nova.'
                  : 'Há alterações não salvas.'}
              </span>
            ) : null}
            <Button
              type="submit"
              variant="primary"
              disabled={!mudou}
              loading={salvarDados.isPending || trocarSenha.isPending}
              icon={<Check {...ICON} size={16} />}
            >
              Salvar alterações
            </Button>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
